// Equipe e organograma, como o do Entra ID visto pelo Teams: a pessoa em
// destaque, a cadeia de gestores acima e os subordinados diretos abaixo.
// Também mostra o organograma completo e a lista por departamento.
// Administradores do Ordo definem gestor, cargo e departamento (no diálogo
// ou arrastando um cartão para cima de outro).

import * as store from "./store.js";
import * as router from "./router.js";
import { api } from "./api.js";
import { el, dialog, toast, popup, safeGet, safeSet } from "./dom.js";
import { ui } from "./icons.js";
import { avatar } from "./people.js";

let mode = safeGet("ordo.teamMode", "person"); // person | full | list
let data = null; // {people, departments, can_manage}

export async function renderTeam(host, route = {}) {
  const tabs = el("div", { class: "picker-tabs" });
  const body = el("div", { class: "team-body" }, el("p", { class: "muted" }, "Carregando…"));
  const search = el("input", { class: "team-search", type: "search", placeholder: "Buscar pessoa, cargo ou departamento" });
  host.replaceChildren(el("div", { class: "list-view wide team-view" },
    el("h1", {}, ui("org", 26), " Equipe"),
    el("p", { class: "muted" }, "Quem é quem no time: cargos, departamentos e a quem cada pessoa responde."),
    el("div", { class: "team-toolbar" }, tabs, search),
    body));
  const focusId = () => route.id && byId(route.id) ? route.id : store.state.me.id;
  const draw = () => {
    tabs.replaceChildren(...[["person", "Organograma"], ["full", "Completo"], ["list", "Lista"]].map(([id, l]) =>
      el("button", { class: "picker-tab" + (mode === id ? " on" : ""), onclick: () => { mode = id; safeSet("ordo.teamMode", id); draw(); } }, l)));
    if (mode === "list") drawList(body, search.value);
    else if (mode === "full") drawFull(body, reload);
    else drawPerson(body, focusId(), reload);
  };
  const reload = async () => {
    try {
      data = await api("GET", "/team");
      draw();
    } catch (err) {
      body.replaceChildren(el("p", { class: "error" }, err.message));
    }
  };
  search.addEventListener("input", () => {
    if (mode === "list") return drawList(body, search.value);
    const q = norm(search.value);
    if (!q || !data) return;
    const hits = data.people.filter((p) => matches(p, q)).slice(0, 8);
    popup(search, hits.length
      ? hits.map((p) => el("button", { class: "menu-item", onclick: () => { search.value = ""; document.querySelector(".popup")?.close?.(); focus(p.id); } },
        avatar(p, "sm"), el("span", {}, p.name), el("span", { class: "mi-hint" }, p.title || p.department || "")))
      : el("p", { class: "muted small team-nohit" }, "Ninguém encontrado."), { className: "menu", nested: false });
    search.focus();
  });
  if (data) draw();
  await reload();
}

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
const matches = (p, q) => [p.name, p.email, p.title, p.department].some((v) => norm(v).includes(q));
const byId = (id) => data?.people.find((p) => p.id === id) || null;
const reportsOf = (id) => data.people.filter((p) => p.manager_id === id);
const focus = (id) => {
  if (mode !== "person") { mode = "person"; safeSet("ordo.teamMode", mode); }
  router.go({ view: "team", id });
};

// Todos abaixo de uma pessoa (para não deixar escolher um deles como gestor).
function below(id) {
  const out = new Set();
  const walk = (x) => { for (const r of reportsOf(x)) if (!out.has(r.id)) { out.add(r.id); walk(r.id); } };
  walk(id);
  return out;
}

function managersOf(person) {
  const chain = [];
  for (let m = byId(person.manager_id); m && m.id !== person.id && !chain.includes(m); m = byId(m.manager_id)) chain.unshift(m);
  return chain;
}

// ---------- Cartões ----------

function card(p, { size = "", reload, onclick } = {}) {
  const n = reportsOf(p.id).length;
  const node = el("div", { class: "org-card" + (size ? " " + size : ""), tabindex: 0, dataset: { id: p.id },
    title: [p.name, p.title, p.department].filter(Boolean).join(" · "),
    onclick: onclick || (() => focus(p.id)),
    onkeydown: (e) => { if (e.key === "Enter") (onclick || (() => focus(p.id)))(); } },
  avatar(p, size === "big" ? "lg" : "md"),
  el("div", { class: "org-card-text" },
    el("strong", {}, p.name, p.id === store.state.me.id ? " (você)" : ""),
    el("span", { class: "muted small" }, p.title || "Sem cargo"),
    p.department ? el("span", { class: "org-dep small" }, p.department) : null),
  n && size !== "big" ? el("span", { class: "org-count", title: `${n} subordinado(s) direto(s)` }, String(n)) : null);
  if (data.can_manage && reload) dragTarget(node, p, reload);
  return node;
}

// Administrador arrasta um cartão para cima de outro: quem foi arrastado passa
// a responder a quem recebeu.
let dragging = null;
function dragTarget(node, p, reload) {
  node.draggable = true;
  node.addEventListener("dragstart", (e) => { dragging = p.id; e.dataTransfer.effectAllowed = "move"; node.classList.add("dragging"); });
  node.addEventListener("dragend", () => { dragging = null; node.classList.remove("dragging"); });
  node.addEventListener("dragover", (e) => {
    if (!dragging || dragging === p.id || below(dragging).has(p.id)) return;
    e.preventDefault();
    node.classList.add("drop");
  });
  node.addEventListener("dragleave", () => node.classList.remove("drop"));
  node.addEventListener("drop", (e) => {
    e.preventDefault();
    node.classList.remove("drop");
    const who = byId(dragging);
    if (who && who.manager_id !== p.id) setManager(who, p, reload);
  });
}

async function setManager(who, manager, reload) {
  try {
    await api("PATCH", `/team/${who.id}`, { manager_id: manager?.id || null });
    toast(manager ? `${who.name} agora responde a ${manager.name}.` : `${who.name} agora está no topo do organograma.`);
    await reload();
  } catch (err) { toast(err.message); }
}

// Soltar aqui tira o gestor (a pessoa vai para o topo).
function topDrop(reload) {
  if (!data.can_manage) return null;
  const zone = el("div", { class: "org-topdrop muted small" }, "Solte aqui para deixar a pessoa sem gestor (topo do organograma)");
  zone.addEventListener("dragover", (e) => { if (dragging && byId(dragging)?.manager_id) { e.preventDefault(); zone.classList.add("drop"); } });
  zone.addEventListener("dragleave", () => zone.classList.remove("drop"));
  zone.addEventListener("drop", (e) => { e.preventDefault(); zone.classList.remove("drop"); const who = byId(dragging); if (who) setManager(who, null, reload); });
  return zone;
}

// ---------- Organograma da pessoa (como no Teams) ----------

function drawPerson(body, id, reload) {
  const p = byId(id);
  if (!p) return body.replaceChildren(el("p", { class: "muted" }, "Pessoa não encontrada."));
  const managers = managersOf(p);
  const reports = reportsOf(p.id);
  const peers = p.manager_id ? reportsOf(p.manager_id).filter((x) => x.id !== p.id) : [];
  const chart = el("div", { class: "org-chart" },
    managers.length ? el("div", { class: "org-chain" }, managers.map((m) => el("div", { class: "org-step" }, card(m, { size: "compact", reload })))) : null,
    el("div", { class: "org-step focus" }, card(p, { size: "big", reload })),
    reports.length
      ? [el("div", { class: "org-label muted small" }, `${reports.length} subordinado(s) direto(s)`),
        el("div", { class: "org-reports" }, reports.map((r) => card(r, { reload })))]
      : el("p", { class: "muted small org-label" }, "Sem subordinados diretos."),
    data.can_manage ? el("p", { class: "muted small org-hint" }, "Dica: arraste um cartão para cima de outro para mudar o gestor.") : null);
  body.replaceChildren(el("div", { class: "team-person" }, chart, details(p, peers, reload)));
}

function details(p, peers, reload) {
  const manager = byId(p.manager_id);
  const line = (icon, content) => content ? el("div", { class: "team-line" }, ui(icon, 14), el("span", {}, content)) : null;
  return el("aside", { class: "team-details" },
    avatar(p, "xl"),
    el("h2", {}, p.name),
    el("div", { class: "muted" }, p.title || "Sem cargo"),
    p.department ? el("div", { class: "org-dep" }, p.department) : null,
    el("div", { class: "team-lines" },
      line("mail", p.email ? el("a", { href: `mailto:${p.email}` }, p.email) : null),
      line("phone", p.phone),
      line("user", manager ? el("span", {}, "Responde a ", el("a", { href: "#", onclick: (e) => { e.preventDefault(); focus(manager.id); } }, manager.name)) : "Topo do organograma"),
      line("admin", p.role === "admin" ? "Administra o Ordo" : null)),
    p.bio ? el("p", { class: "team-bio" }, p.bio) : null,
    peers.length ? el("div", { class: "team-peers" }, el("h4", {}, `Trabalha com (${peers.length})`),
      peers.slice(0, 12).map((x) => el("button", { class: "person-row team-peer", onclick: () => focus(x.id) }, avatar(x, "sm"),
        el("span", { class: "grow" }, x.name), el("span", { class: "muted small" }, x.title || "")))) : null,
    data.can_manage ? el("button", { class: "primary small", onclick: () => editDialog(p, reload) }, ui("userCog", 14), " Editar posição") : null);
}

// ---------- Organograma completo ----------

function drawFull(body, reload) {
  const roots = data.people.filter((p) => !p.manager_id);
  // Pessoas sem gestor e sem ninguém abaixo ficam juntas no fim, para não
  // espalhar o desenho.
  const tops = roots.filter((r) => reportsOf(r.id).length);
  const loose = roots.filter((r) => !reportsOf(r.id).length);
  const branch = (p, seen) => {
    const kids = seen.has(p.id) ? [] : reportsOf(p.id);
    seen.add(p.id);
    return el("li", {}, card(p, { size: "compact", reload }), kids.length ? el("ul", {}, kids.map((k) => branch(k, seen))) : null);
  };
  const seen = new Set();
  body.replaceChildren(
    topDrop(reload),
    tops.length
      ? el("div", { class: "org-full-wrap" }, el("div", { class: "org-full" }, el("ul", {}, tops.map((r) => branch(r, seen)))))
      : el("p", { class: "muted" }, data.can_manage
        ? "Ainda não há estrutura. Abra uma pessoa e use \"Editar posição\", ou arraste os cartões abaixo um para cima do outro."
        : "Ainda não há estrutura. Quem administra o Ordo monta o organograma."),
    loose.length ? el("h3", {}, `Sem gestor definido (${loose.length})`) : null,
    loose.length ? el("div", { class: "org-reports loose" }, loose.map((p) => card(p, { size: "compact", reload }))) : null);
}

// ---------- Lista por departamento ----------

function drawList(body, q) {
  if (!data) return;
  const query = norm(q);
  const list = data.people.filter((p) => !query || matches(p, query));
  const groups = new Map();
  for (const p of list) {
    const k = p.department || "";
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(p);
  }
  const keys = [...groups.keys()].sort((a, b) => (!a) - (!b) || a.localeCompare(b, "pt-BR"));
  body.replaceChildren(...(list.length ? keys.map(groupOf) : [el("p", { class: "muted" }, "Ninguém encontrado.")]));
  function groupOf(k) {
    return el("section", { class: "team-group" },
      el("h3", {}, k || "Sem departamento", el("span", { class: "muted" }, ` · ${groups.get(k).length}`)),
      groups.get(k).map((p) => el("button", { class: "person-row team-row", onclick: () => focus(p.id) },
        avatar(p, "md"),
        el("div", { class: "grow" }, el("strong", {}, p.name), el("div", { class: "muted small" }, p.title || "Sem cargo")),
        el("span", { class: "muted small" }, byId(p.manager_id) ? `Responde a ${byId(p.manager_id).name}` : "Topo"),
        data.can_manage ? el("span", { class: "icon-btn tiny", title: "Editar posição", onclick: (e) => { e.stopPropagation(); editDialog(p, () => api("GET", "/team").then((d) => { data = d; drawList(body, q); })); } }, ui("userCog", 14)) : null)));
  }
}

// ---------- Editar posição (administradores) ----------

export function editDialog(p, reload) {
  const blocked = below(p.id);
  const manager = el("select", {},
    el("option", { value: "" }, "Ninguém (topo do organograma)"),
    data.people.filter((x) => x.id !== p.id && !blocked.has(x.id)).map((x) =>
      el("option", { value: x.id, selected: x.id === p.manager_id }, x.title ? `${x.name} · ${x.title}` : x.name)));
  const title = el("input", { value: p.title || "", maxlength: 80, placeholder: "Ex.: Desenvolvedora pleno" });
  const listId = "team-deps";
  const dep = el("input", { value: p.department || "", maxlength: 80, placeholder: "Ex.: Engenharia", list: listId });
  const deps = el("datalist", { id: listId }, data.departments.map((d) => el("option", { value: d })));
  const reports = new Set(reportsOf(p.id).map((r) => r.id));
  const ancestors = new Set(managersOf(p).map((m) => m.id));
  const reportBox = el("div", { class: "people-pick" });
  const drawReports = () => reportBox.replaceChildren(
    ...data.people.filter((x) => x.id !== p.id && !ancestors.has(x.id)).map((x) => {
      const box = el("input", { type: "checkbox", checked: reports.has(x.id), onchange: (e) => (e.target.checked ? reports.add(x.id) : reports.delete(x.id)) });
      const cur = byId(x.manager_id);
      return el("label", { class: "person-row" }, box, avatar(x, "sm"), el("span", { class: "grow" }, x.name),
        el("span", { class: "muted small" }, cur && cur.id !== p.id ? `hoje responde a ${cur.name}` : ""));
    }));
  drawReports();
  dialog((close) => [
    el("h3", {}, `Posição de ${p.name}`),
    el("label", { class: "field" }, "Cargo", title),
    el("label", { class: "field" }, "Departamento", dep, deps),
    el("label", { class: "field" }, "Gestor (a quem responde)", manager),
    el("h4", {}, "Subordinados diretos"),
    el("p", { class: "muted small" }, "Marque quem responde a esta pessoa. Quem já tinha outro gestor passa a responder a ela."),
    reportBox,
    el("div", { class: "dlg-actions" }, el("button", { onclick: close }, "Cancelar"),
      el("button", { class: "primary", onclick: async () => {
        try {
          await api("PATCH", `/team/${p.id}`, { manager_id: manager.value || null, title: title.value, department: dep.value });
          const before = new Set(reportsOf(p.id).map((r) => r.id));
          for (const id of reports) if (!before.has(id)) await api("PATCH", `/team/${id}`, { manager_id: p.id });
          for (const id of before) if (!reports.has(id)) await api("PATCH", `/team/${id}`, { manager_id: null });
          close();
          toast("Organograma atualizado.");
          store.loadTree().catch(() => {});
          await reload();
        } catch (err) { toast(err.message); }
      } }, "Salvar")),
  ], { wide: true });
  requestAnimationFrame(() => title.focus());
}
